<template>
  <div>
    <v-progress-circular
      v-if="isDataLoading"
      :size="100"
      :width="7"
      color="primary"
      indeterminate
      class="mt-3 mb-3"
    ></v-progress-circular>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { APP_SPECIFIC_KEYS } from '@/services/util.service';

export default {
     data() {
      return {
        isDataLoading: true,
        popUp: false,
        hasRedirected: false
      };
    },
  computed: {
    ...mapGetters([
      'getStore',
      'isStoreLoading',
      'getAvailableMenus',
      'isMenuChannelAllowed',
      'getOrderType',
      'getLiteMenus',
      'getMenus'
    ]),
      filteredMenus() {
        try {
          if (this.getAvailableMenus && this.getAvailableMenus.length && this.getLiteMenus && this.getLiteMenus.length) {
            const filteredMenus = this.getAvailableMenus.filter(menu => {
              return this.getLiteMenus.some(liteMenu => liteMenu.code === menu.code);
            });
            if (filteredMenus.length == 0){
              this.handleApiLog('Scan filteredMenus is an empty array. getAvailableMenus and getLiteMenus empty. These are the two menus', {
                getAvailableMenus: this.getAvailableMenus,
                fetchLiteMenus: this.getLiteMenus
              })
            }
            return filteredMenus;
          } else {
            if (!this.getAvailableMenus) {
              this.handleApiLog('Scan !getAvailableMenus. This is getMenus without filters', this.getMenus)
            } 
            if (this.getAvailableMenus.length == 0) {
              this.handleApiLog('Scan getAvailableMenus is an empty array. This is getMenus without filters', this.getMenus)
            } 
            return [];
          }
        } catch (e) {
          this.handleApiError(e);
          return [];
        }
      }
  },
  watch: {
    // Watch for changes in getOrderType and trigger a re-fetch if needed
    getOrderType: {
      handler: 'refetchMenusIfNeeded',
      immediate: true // Trigger the handler immediately on component mount
    },
  },
  async created() {
    const currentStoreId =  this.$route.params.sid || (this.getStore && this.getStore.storeId) || this.$route.query.storeId;
    if (currentStoreId) {
      let authUser = null;
      try {
        const vuexData = localStorage.getItem('vuex');
        if (vuexData) {
          const parsedData = JSON.parse(vuexData);
          if (parsedData && parsedData.auth) {
            authUser = parsedData.auth;
          }
        }
      } catch (authErr) {
        console.error('Error preserving auth data', authErr);
      }
      sessionStorage.clear();

      // clearing only app-specific localStorage keys instead of clearing all
      try {
        for (const key of APP_SPECIFIC_KEYS) {
          localStorage.removeItem(key);
        }
      } catch (e) {
        console.error('Error clearing app-specific localStorage keys', e);
      }
      
      if (authUser) {
        localStorage.setItem('vuex', JSON.stringify({ auth: authUser }));
      }
      if (currentStoreId) {
        localStorage.setItem('lastStoreId', currentStoreId);
      }
      this.setDeliveryAddress(null)
      this.resetCart()
      if (window.indexedDB && indexedDB.databases) {
        indexedDB.databases().then(dbs => {
          dbs.forEach(db => {
            try {
              indexedDB.deleteDatabase(db.name);
            } catch (e) {
              console.error('Error clearing IndexedDB:', e);
            }
          });
        }).catch(e => console.error('Error listing IndexedDB databases:', e));
      }
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            try {
              caches.delete(name);
            } catch (e) {
              console.error('Error clearing cache:', e);
            }
          });
        }).catch(e => console.error('Error listing caches:', e));
      }
      document.cookie.split(";").forEach(cookie => {
        try {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name) {
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
          }
        } catch (e) {
          console.error('Error clearing cookie:', e);
        }
      });
    }
    this.waitForQueryAndSetTableId();
    try {
      const { sid } = this.$route.params;
      const tid = this.$route.query.tid || this.$route.params.tid;
      await this.fetchLiteMenus();
      if (!this.getLiteMenus) {
        this.handleApiLog('MenuSelect !getLiteMenus', this.getLiteMenus);
      } 
      
      if (this.getLiteMenus !== null && this.getLiteMenus && this.getLiteMenus.length == 0) {
        this.handleApiLog('MenuSelect getLiteMenus empty', this.getLiteMenus);
      } 
      
      if (this.getAvailableMenus && this.getLiteMenus && 
          this.getAvailableMenus.length && this.getLiteMenus.length) {
        if (this.getAvailableMenus.length > 1 && !this.popUp) {
          this.popUp = true;
          if (this.$route.name !== 'menu-selection') {
            let query = this.$route.query;
            const dineInOnlyQueryParam = query.dineInOnly;
            query.tid = tid;
            const vc = this.$route.query.vc;
            this.$router.replace({
              name: 'menu-selection',
              params: {
                sid,
                restaurantSlug: this.getStore.slug
              },
              query: {
                tid,
                dineInOnly: dineInOnlyQueryParam,
                vc
              }
            });
          }
        } else {
          this.redirectToSingleMenu();
        }
      } else {
        if (this.getAvailableMenus !== null && this.getLiteMenus !== null) {
          this.popUp = true;
          this.clearMenu(this.$vuetify.t('$vuetify.menu.noMenu'));
        }
      }
    } catch (error) {
      this.handleApiError(error);
      console.error('Error in created:', error);
    } finally {
      this.isDataLoading = false;
    }
  },
  methods: {
    waitForQueryAndSetTableId() {
      if (this.$route.params.tid) {
        this.$store.dispatch("setTableId", this.$route.params.tid);
        this.$store.dispatch("setOrderType", "DINE_IN")
      }

      if (this.$route.query.vc !== undefined) {
        this.$store.dispatch("fetchShoppingCart");
      } else {
        setTimeout(() => {
          if (this.$route.query.vc !== undefined) {
            this.$store.dispatch("fetchShoppingCart");
          }
        }, 500); 
      }
    },
    ...mapActions(['setDineInOnly', 'clearMenu', 'fetchLiteMenus', 'resetCart', 'setDeliveryAddress']),
      async refetchMenusIfNeeded() {
        // Check the flag that you want to trigger a re-fetch
        if (this.getOrderType) {
          await this.fetchLiteMenus();
        }
      },
      async handleApiError(error) {
        try {
          this.$api.post('/order/error', {
            error: 'Scan ERROR' + error,
            storeId: this.getStore.storeId
          });
        } catch (postError) {
          console.error('Error while handling API error:', postError);
        }
      },
    async handleApiLog(part, log) {
      let formattedMsg;
      if (Array.isArray(log)) {
        const formattedLog = log.map(item => {
          if (typeof item === 'object') {
            return JSON.stringify(item);
          }
          return String(item);
        });
        formattedMsg = `Scan ${part} ${formattedLog.join(', ')}`;
      } else if (typeof log === 'object') {
        formattedMsg = `Scan ${part} ${JSON.stringify(log)}`;
      } else {
        formattedMsg = `Scan ${part} ${String(log)}`;
      }
    },
    redirectToSingleMenu: _.debounce(function() {
        if (this.hasRedirected) return;
        this.hasRedirected = true;
        const referrerParam = this.referrer ? { referrer: this.referrer } : {};
        const referrerPath = this.$route.query.referrer;
        const { sid } = this.$route.params;
        const dineInOnlyQueryParam = this.$route.query.dineInOnly;
        const tid = this.$route.query.tid || this.$route.params.tid;
        const vc = this.$route.query.vc;
        const targetRoute = {
          name: 'store',
          params: {
              sid,
              mid: this.filteredMenus[0].code,
              restaurantSlug: this.getStore.slug,
              ...referrerParam
          },
          query: {
              dineInOnly: dineInOnlyQueryParam,
              tid,
              skipBeforeEnter: true,
              vc
          }
        };
        if (
          this.$route.name === targetRoute.name &&
          JSON.stringify(this.$route.params) === JSON.stringify(targetRoute.params) &&
          JSON.stringify(this.$route.query) === JSON.stringify(targetRoute.query)
        ) {
            console.log('Navigation skipped: Already at the target route.');
            return;
        }

        if (this.filteredMenus && this.filteredMenus.length === 1) {
            localStorage.setItem('forcePopup', JSON.stringify(true));
            if (referrerParam || referrerPath) {
                localStorage.setItem('forcePopupReferral', JSON.stringify(true));
            }
            this.$router.replace(targetRoute);
        }
    }, 500)
  }
};
</script>